import { isVideoStation } from '../../../../../core/stations/helpers';
import { useActiveStation } from '../../../../../core/stations/hooks/useActiveStation';
import { DiarizationProvider } from '../diarization/DiarizationContext';
import { useIsStreamRangeInFuture } from '../hooks/range/useIsStreamRangeInFuture';
import { useIsStreamRangeTooRecent } from '../hooks/range/useIsStreamRangeTooRecent';
import { useAdjustFocusForPartialSegmentCoverage } from '../hooks/segments/useAdjustFocusForPartialSegmentCoverage';
import { useStreamSegments } from '../hooks/segments/useStreamSegmentsQuery';
import { useSyncSegmentsRange } from '../hooks/segments/useSyncSegmentsRange';
import { LoggerAudioMode } from '../modes/audio/LoggerAudioMode';
import { LoggerVideoMode } from '../modes/video/LoggerVideoMode';
import { useStreamUserDefinedRange } from '../stores/stream-screen.store';
import { LoggerLoader } from './content-loader/ContentLoader';
import { FutureRangeMessage } from './FutureRangeMessage';
import { NoSegmentsMessage } from './NoSegmentsMessage';
import { StreamTransition } from './StreamTransition';
import { TooRecentRangeMessage } from './TooRecentRangeMessage';

/**
 * Stream content component that renders the video or audio mode.
 * Handles segments loading state centrally, showing a loader while segments are being fetched.
 * Shows FutureRangeMessage when the selected range is entirely in the future (no fetch).
 * Shows TooRecentRangeMessage when the range start is within the minimum lookback (e.g. 20 min).
 */
export function StreamContent() {
  const station = useActiveStation();
  const isVideo = isVideoStation(station);
  const { isLoading: isLoadingSegments, segments } = useStreamSegments();
  const userRange = useStreamUserDefinedRange();
  const isFutureRange = useIsStreamRangeInFuture();
  const isTooRecent = useIsStreamRangeTooRecent();

  // Sync segments range to store for validation purposes
  useSyncSegmentsRange();
  useAdjustFocusForPartialSegmentCoverage();

  // Single transition key and content so one AnimatePresence coordinates exit/enter across all branches
  let transitionKey: string;
  let content: React.ReactNode;

  if (isLoadingSegments) {
    transitionKey = 'loading';
    content = <LoggerLoader />;
  } else if (isFutureRange) {
    transitionKey = 'future';
    content = <FutureRangeMessage />;
  } else if (isTooRecent) {
    transitionKey = 'too-recent';
    content = <TooRecentRangeMessage />;
  } else if (!segments.length) {
    transitionKey = 'no-segments';
    content = <NoSegmentsMessage />;
  } else {
    transitionKey = `${station.slug}-${userRange.from}-${userRange.to}`;
    content = (
      <DiarizationProvider>
        {isVideo ? <LoggerVideoMode /> : <LoggerAudioMode />}
      </DiarizationProvider>
    );
  }

  return <StreamTransition transitionKey={transitionKey}>{content}</StreamTransition>;
}
